import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, Button, Menu, MenuItem, Popover } from '@mui/material';
import CommonDatePicker from '../../../form-components/CommonDatePicker';
import { CONSULT_RESULT_OPTIONS, CONSULT_RESULT_COLORS } from '../../../constants/consultation';
import {
  PageContainer,
  HeaderRow,
  HeaderCountsRow,
  HeaderLeftGroup,
  HeaderCenterGroup,
  HeaderRightGroup,
  DateNavContainer,
  DateTitle,
  ViewSegmentButton,
  HeaderSummaryChips,
  NavSideActions,
  SummaryChipItem,
  SummaryChipDivider,
  ATTEND_STATUS_META,
  SUMMARY_EXTRA_COUNT_META,
} from './PrivateLessonStyled';
import {
  TYPE_FILTER_ALL,
  TYPE_FILTER_GROUP,
  TYPE_FILTER_PRIVATE,
  TYPE_FILTER_OT,
  TYPE_FILTER_CONSULT,
  TYPE_FILTER_ETC,
} from './lessonStatus';
import { FILTER_VALUE_TO_COUNT_KEY } from './branchScheduleSetting';
import { formatWeekTitle } from '../weekStart';
import {
  TodayButton,
  NavButton,
  FilterButton,
  SettingsButton,
  ViewToggleContainer,
  ZoomButtonGroup,
  ZoomButton,
  ZoomLabel,
  CalendarPickerButton,
  RangePickerButton,
  RangeResetButton,
  HeaderAddScheduleButton,
} from '../LessonCalendarStyled';

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토']; // moment().day() 순서

// 네비게이션 화살표 아이콘
const LeftArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.5292 12.4701C10.6701 12.6109 10.7492 12.802 10.7492 13.0013C10.7492 13.2006 10.6701 13.3917 10.5292 13.5326C10.3883 13.6734 10.1972 13.7526 9.99794 13.7526C9.79868 13.7526 9.60758 13.6734 9.46669 13.5326L4.46668 8.53255C4.39676 8.46287 4.34129 8.38008 4.30343 8.28891C4.26558 8.19775 4.24609 8.10001 4.24609 8.0013C4.24609 7.90259 4.26558 7.80485 4.30343 7.71369C4.34129 7.62252 4.39676 7.53973 4.46668 7.47005L9.46669 2.47005C9.60758 2.32915 9.79868 2.25 9.99794 2.25C10.1972 2.25 10.3883 2.32915 10.5292 2.47005C10.6701 2.61095 10.7492 2.80204 10.7492 3.0013C10.7492 3.20056 10.6701 3.39165 10.5292 3.53255L6.06106 8.00068L10.5292 12.4701Z"
      fill="#A6A6A6"
    />
  </svg>
);

const RightArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5.47081 3.52995C5.32992 3.38905 5.25077 3.19796 5.25077 2.9987C5.25077 2.79944 5.32992 2.60835 5.47081 2.46745C5.61171 2.32655 5.80281 2.2474 6.00206 2.2474C6.20132 2.2474 6.39242 2.32655 6.53331 2.46745L11.5333 7.46745C11.6032 7.53713 11.6587 7.61992 11.6966 7.71109C11.7344 7.80225 11.7539 7.89999 11.7539 7.9987C11.7539 8.09741 11.7344 8.19515 11.6966 8.28631C11.6587 8.37748 11.6032 8.46027 11.5333 8.52995L6.53331 13.5299C6.39242 13.6709 6.20132 13.75 6.00206 13.75C5.80281 13.75 5.61171 13.6709 5.47081 13.5299C5.32992 13.3891 5.25077 13.198 5.25077 12.9987C5.25077 12.7994 5.32992 12.6083 5.47081 12.4674L9.93894 7.99932L5.47081 3.52995Z"
      fill="#A6A6A6"
    />
  </svg>
);

// 요약 칩 = 출석 상태 필터 토글 (다중 선택, 전부 꺼짐 = 전체 보기)
// countKey = 지점 스케줄 설정의 요약 카운트 표시 키
const ATTEND_FILTER_SEGMENTS = [
  { value: 'reserved', label: '예약', countKey: 'RESERVED' },
  { value: 'attended', label: '출석', countKey: 'ATTENDED' },
  { value: 'absented', label: '결석', countKey: 'ABSENT' },
];

// 전체 보기에서만 쓰는 유형 카테고리 칩 — value 는 칩 필터 값이자 SUMMARY_EXTRA_COUNT_META 키
const CATEGORY_SEGMENTS = [
  { value: 'group', label: '그룹', countKey: 'GROUP', summaryKey: 'groupCount' },
  { value: 'ot', label: 'OT', countKey: 'OT', summaryKey: 'otCount' },
  { value: 'consult', label: '상담', countKey: 'CONSULT', summaryKey: 'consultCount' },
  { value: 'etc', label: '기타', countKey: 'ETC', summaryKey: 'etcCount' },
];

// 수업 유형 세그먼트 (단일 선택) — '개인'은 개인레슨 + 듀엣레슨, OT 는 별도
const TYPE_SEGMENTS = [
  { value: TYPE_FILTER_ALL, label: '전체' },
  { value: TYPE_FILTER_GROUP, label: '그룹' },
  { value: TYPE_FILTER_PRIVATE, label: '개인' },
  { value: TYPE_FILTER_OT, label: 'OT' },
  { value: TYPE_FILTER_CONSULT, label: '상담' },
  { value: TYPE_FILTER_ETC, label: '기타' },
];

// 상담 결과 칩 — 라벨·색은 결과 메타와 동일 (CONSULT_RESULT_COLORS)
const CONSULT_RESULT_SEGMENTS = CONSULT_RESULT_OPTIONS.map(option => ({
  value: option.value,
  label: option.label,
  color: CONSULT_RESULT_COLORS[option.value],
}));

// 그룹 예약 상태 칩 — 그룹레슨 화면의 상태색과 동일 톤
const GROUP_STATUS_SEGMENTS = [
  { key: 'groupFull', status: 'full', label: '예약마감', color: '#247E46' },
  { key: 'groupUnder', status: 'under', label: '예약미달', color: '#B96800' },
  { key: 'groupNone', status: 'none', label: '예약없음', color: '#9E230B' },
];

// 뷰 전환 세그먼트 정의 (월/주/일(강사별))
const VIEW_SEGMENTS = [
  { value: 'month', label: '월' },
  { value: 'week', label: '주', isCompact: false },
  { value: 'day', label: '일(직원별)', isCompact: true },
];

/**
 * 개인 레슨 헤더 컴포넌트
 * - 오늘 버튼 + 뷰별 줌 컨트롤
 * - 날짜 네비게이션 (월=±1개월 / 주=±1주 / 일=±1일)
 * - 뷰 전환 3단 토글 (월/주/일(직원별))
 * - 요약 카운트 칩 = 출석 상태 필터 토글 (예약/출석/결석, 다중 선택)
 * - 임직원 필터 버튼, 지점 스케줄 설정 버튼
 */
const PrivateLessonHeader = ({
  view,
  currentDate,
  weekDates,
  selectedTrainerIds,
  summaryCounts,
  attendFilters,
  onToggleAttendFilter,
  consultResultFilters = [],
  onToggleConsultResultFilter,
  groupStatusFilters = [],
  onToggleGroupStatusFilter,
  onResetFilters,
  typeFilter,
  onTypeFilterChange,
  onTodayClick,
  onNavigate,
  onViewChange,
  onTrainerFilterClick,
  onSettingsClick,
  visibleCounts = [],
  onDateChange,
  // 기간 조회 (최대 31일, 월간 그리드로 표시)
  customRange,
  onApplyRange,
  onClearRange,
  // 줌 컨트롤 (현재 뷰의 줌 상태를 List가 내려줌)
  zoomPercent,
  canZoomIn,
  canZoomOut,
  onZoomIn,
  onZoomOut,
  onAddSchedule,
}) => {
  const { t } = useTranslation();
  const dateInputRef = useRef(null);
  const [typeAnchor, setTypeAnchor] = useState(null);
  // 설정에서 숨긴 유형(그룹·OT·상담·기타)은 드롭다운에서도 뺀다. 전체·개인은 항상 있다.
  const typeSegments = TYPE_SEGMENTS.filter(segment => {
    const countKey = FILTER_VALUE_TO_COUNT_KEY[segment.value];
    return !countKey || visibleCounts.includes(countKey);
  });
  const selectedTypeLabel = TYPE_SEGMENTS.find(segment => segment.value === typeFilter)?.label ?? '전체';

  // 기간 조회 팝오버 — 적용 전까지는 draft 로만 들고 있다가 조회를 눌러야 반영
  const [rangeAnchor, setRangeAnchor] = useState(null);
  const [draftStart, setDraftStart] = useState(null);
  const [draftEnd, setDraftEnd] = useState(null);

  const openRangePopover = e => {
    setDraftStart(customRange ? moment(customRange.start).toDate() : null);
    setDraftEnd(customRange ? moment(customRange.end).toDate() : null);
    setRangeAnchor(e.currentTarget);
  };

  const applyRange = () => {
    const ok = onApplyRange?.(draftStart ? moment(draftStart) : null, draftEnd ? moment(draftEnd) : null);
    if (ok) setRangeAnchor(null);
  };

  // 날짜 표시 텍스트 (기간 조회 우선, 그 외 뷰별 포맷)
  const getDateDisplayText = () => {
    if (customRange) {
      const startM = moment(customRange.start);
      const endM = moment(customRange.end);
      return `${startM.format('YYYY.MM.DD')} ~ ${endM.format(endM.isSame(startM, 'year') ? 'MM.DD' : 'YYYY.MM.DD')}`;
    }
    if (view === 'month') {
      return currentDate.format('YYYY년 MM월');
    }
    // 주간은 그룹 스케줄 주 제목과 같은 표기(연도가 걸치면 양쪽 연도 표기)
    if (view === 'week') {
      return formatWeekTitle(weekDates[0], weekDates[6]);
    }
    return `${currentDate.format('YYYY년 MM월 DD일')} (${DAY_NAMES[currentDate.day()]})`;
  };

  // 요약 카운트 칩 — 칩 사이 구분선은 보이는 칩끼리만 넣는다(맨 앞 칩이 꺼져도 '|' 가 남지 않게)
  const isCountVisible = key => visibleCounts.includes(key);
  const renderToggleChip = ({ key, label, color, isActive, count, onClick }) => (
    <SummaryChipItem
      key={key}
      className="hdr-chip"
      type="button"
      countColor={color}
      isActive={isActive}
      aria-pressed={isActive}
      title={isActive ? `${label} 필터 해제` : `${label}만 보기`}
      onClick={onClick}>
      {label}
      <b>{count}</b>
    </SummaryChipItem>
  );

  const chipItems = [];
  if (summaryCounts) {
    // 스케줄 칩 = 전체 복귀 (유형·서브 필터 일괄 해제)
    if (isCountVisible('SCHEDULE')) {
      chipItems.push(
        <SummaryChipItem
          key="schedule"
          className="hdr-chip"
          type="button"
          countColor={SUMMARY_EXTRA_COUNT_META.schedule.color}
          isActive={false}
          title="전체 보기"
          onClick={() => onResetFilters?.()}>
          스케줄<b>{summaryCounts.scheduleCount}건</b>
        </SummaryChipItem>,
      );
    }

    // 예약/출석/결석은 개인·듀엣(유형=OT 면 OT) — 그룹·상담은 상태 개념이 없어 건수로 따로 센다
    if (typeFilter === TYPE_FILTER_ALL || typeFilter === TYPE_FILTER_PRIVATE || typeFilter === TYPE_FILTER_OT) {
      ATTEND_FILTER_SEGMENTS.filter(segment => isCountVisible(segment.countKey)).forEach(segment =>
        chipItems.push(
          renderToggleChip({
            key: segment.value,
            label: segment.label,
            color: ATTEND_STATUS_META[segment.value].countColor,
            isActive: attendFilters.includes(segment.value),
            count: summaryCounts[segment.value],
            onClick: () => onToggleAttendFilter(segment.value),
          }),
        ),
      );
    }

    if (typeFilter === TYPE_FILTER_ALL) {
      CATEGORY_SEGMENTS.filter(segment => isCountVisible(segment.countKey)).forEach(segment =>
        chipItems.push(
          renderToggleChip({
            key: segment.value,
            label: segment.label,
            color: SUMMARY_EXTRA_COUNT_META[segment.value].color,
            isActive: attendFilters.includes(segment.value),
            count: `${summaryCounts[segment.summaryKey]}건`,
            onClick: () => onToggleAttendFilter(segment.value),
          }),
        ),
      );
    }

    // 유형=그룹: 예약 상태별 건수 = 상태 필터 토글
    if (typeFilter === TYPE_FILTER_GROUP) {
      GROUP_STATUS_SEGMENTS.forEach(segment =>
        chipItems.push(
          renderToggleChip({
            key: segment.key,
            label: segment.label,
            color: segment.color,
            isActive: groupStatusFilters.includes(segment.status),
            count: `${summaryCounts[segment.key]}건`,
            onClick: () => onToggleGroupStatusFilter?.(segment.status),
          }),
        ),
      );
    }

    // 유형=상담: 결과별 건수 = 결과 필터 토글 (드롭다운 대체)
    if (typeFilter === TYPE_FILTER_CONSULT) {
      CONSULT_RESULT_SEGMENTS.forEach(segment =>
        chipItems.push(
          renderToggleChip({
            key: segment.value,
            label: segment.label,
            color: segment.color,
            isActive: consultResultFilters.includes(segment.value),
            count: summaryCounts.consultByResult?.[segment.value] ?? 0,
            onClick: () => onToggleConsultResultFilter?.(segment.value),
          }),
        ),
      );
    }
  }

  return (
    <PageContainer>
      {/* 1행: 설정 | ← 날짜 → 오늘 | 뷰 전환 — 그룹레슨 헤더처럼 내비게이션만 중앙에 */}
      <HeaderRow className="hdr-row">
      <HeaderCenterGroup>
        <DateNavContainer>
          <NavButton className="btn-calendar hdr-nav" onClick={() => onNavigate('dec')}>
            <LeftArrowIcon />
          </NavButton>
          <DateTitle className="hdr-date" variant="h5">
            {getDateDisplayText()}
          </DateTitle>
          <NavButton className="btn-calendar hdr-nav" onClick={() => onNavigate('inc')}>
            <RightArrowIcon />
          </NavButton>
          {/* 보조 버튼은 absolute 로 흐름에서 빼서 ‹ 날짜 › 가 화면 정중앙에 오게 한다 (2행 카운트와 세로축 일치) */}
          <NavSideActions>
            <TodayButton className="hdr-today hdr-compact" onClick={onTodayClick}>
              오늘
            </TodayButton>
          </NavSideActions>
        </DateNavContainer>
      </HeaderCenterGroup>
        {/* 지점 스케줄 설정 — 1행 왼쪽 끝(아래 행 확대 비율 바로 위). 예전 '시간 설정'처럼 권한과 무관하게 보인다 */}
        {onSettingsClick && (
          <Box
            className="hdr-settings-wrap"
            sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }}>
            <SettingsButton
              className="hdr-settings hdr-compact"
              startIcon={<SettingsOutlinedIcon sx={{ fontSize: '16px !important' }} />}
              sx={{ ml: '0 !important', '& .MuiButton-startIcon': { mr: '4px' } }}
              onClick={onSettingsClick}>
              {t('스케줄 설정')}
            </SettingsButton>
          </Box>
        )}
        {/* 월/주/일 전환 — 1행(오늘·시간 설정과 같은 줄) 오른쪽 끝, 아래 행 임직원 필터 바로 위 */}
        <Box
          className="hdr-viewtoggle-wrap"
          sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}>
          <ViewToggleContainer className="hdr-viewtoggle">
            {VIEW_SEGMENTS.map((segment, index) => (
              <ViewSegmentButton
                key={segment.value}
                type="button"
                isActive={view === segment.value}
                hasBorder={index > 0}
                isCompact={segment.isCompact}
                onClick={() => onViewChange(segment.value)}>
                {t(segment.label)}
              </ViewSegmentButton>
            ))}
          </ViewToggleContainer>
        </Box>
      </HeaderRow>

      {/* 2행: 스케줄표 바로 위 — 줌·날짜피커(좌) | 요약 카운트(중앙) | 필터·뷰 전환(우) */}
      <HeaderCountsRow>
      <HeaderLeftGroup>
        <ZoomButtonGroup className="hdr-zoom">
          <ZoomButton isDisabled={!canZoomOut} onClick={onZoomOut} disabled={!canZoomOut}>
            −
          </ZoomButton>
          <ZoomLabel>{zoomPercent}%</ZoomLabel>
          <ZoomButton isDisabled={!canZoomIn} onClick={onZoomIn} disabled={!canZoomIn}>
            +
          </ZoomButton>
        </ZoomButtonGroup>

        {/* 기간 조회 — 월/주 전용 (일간은 날짜 피커로 충분). 최대 31일, 선택되면 버튼 테두리 활성 + 초기화 버튼 노출 */}
        {view !== 'day' && (
          <>
            <RangePickerButton
              className="hdr-range"
              type="button"
              isActive={!!customRange}
              title="기간 조회"
              onClick={openRangePopover}>
              <DateRangeIcon style={{ fontSize: '20px' }} />
            </RangePickerButton>
            {customRange && (
              <RangeResetButton type="button" onClick={onClearRange}>
                {t('초기화')}
              </RangeResetButton>
            )}
            <Popover
              open={!!rangeAnchor}
              anchorEl={rangeAnchor}
              onClose={() => setRangeAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              transitionDuration={{ enter: 180, exit: 120 }}
              slotProps={{
                paper: {
                  sx: {
                    mt: '6px',
                    borderRadius: '12px',
                    border: '1px solid #E5E5E5',
                    boxShadow: '0 8px 28px rgba(0, 0, 0, 0.12)',
                  },
                },
              }}>
              <div style={{ padding: '18px 20px 16px' }}>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#262626', marginBottom: '12px' }}>
                  {t('기간 조회')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <CommonDatePicker
                    value={draftStart}
                    onChange={setDraftStart}
                    placeholder="시작일"
                    sx={{ width: '164px' }}
                  />
                  <span style={{ color: '#B0B4BA' }}>~</span>
                  <CommonDatePicker
                    value={draftEnd}
                    onChange={setDraftEnd}
                    placeholder="종료일"
                    minDate={draftStart}
                    sx={{ width: '164px' }}
                  />
                </div>
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#8A8F98' }}>
                  최대 31일까지 조회할 수 있습니다.
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
                  <Button
                    variant="outlined"
                    sx={{
                      borderColor: '#ddd',
                      color: '#666',
                      borderRadius: '8px',
                      height: '36px',
                      px: 2.5,
                      '&:hover': { borderColor: '#bbb', backgroundColor: '#f9f9f9' },
                    }}
                    onClick={() => setRangeAnchor(null)}>
                    {t('취소')}
                  </Button>
                  <TodayButton onClick={applyRange}>{t('조회')}</TodayButton>
                </div>
              </div>
            </Popover>
          </>
        )}

        {/* 날짜 피커는 일(직원별) 보기 전용 — 월/주는 네비게이션만으로 충분 */}
        {view === 'day' && !customRange && (
          <CalendarPickerButton className="hdr-picker" onClick={() => dateInputRef.current?.showPicker()}>
            <CalendarMonthIcon style={{ fontSize: '20px' }} />
            <input
              ref={dateInputRef}
              type="date"
              value={currentDate.format('YYYY-MM-DD')}
              onChange={e => {
                if (e.target.value) onDateChange?.(e.target.value);
              }}
              style={{
                position: 'absolute',
                opacity: 0,
                width: 0,
                height: 0,
                pointerEvents: 'none',
              }}
            />
          </CalendarPickerButton>
        )}

        {/* 일간 전용 — 월/주는 상세 패널 위에 같은 버튼이 이미 있다 */}
        {view === 'day' && onAddSchedule && (
          <HeaderAddScheduleButton className="hdr-add" type="button" onClick={onAddSchedule}>
            + {t('일정 추가')}
          </HeaderAddScheduleButton>
        )}
      </HeaderLeftGroup>

      {/* 중앙: 요약 카운트 — 지점 스케줄 설정에서 끈 칩은 숨긴다 */}
      {chipItems.length > 0 ? (
        <HeaderSummaryChips className="hdr-chips">
          {chipItems.map((chip, index) => (
            <React.Fragment key={chip.key}>
              {index > 0 && <SummaryChipDivider>|</SummaryChipDivider>}
              {chip}
            </React.Fragment>
          ))}
        </HeaderSummaryChips>
      ) : (
        <span />
      )}

        <HeaderRightGroup>
        {/* 유형 드롭다운 — '스케줄: 전체' (상담 필터와 같은 칩 스타일) */}
        <FilterButton
          className="hdr-typefilter"
          isActive={typeFilter !== TYPE_FILTER_ALL}
          endIcon={<ArrowDropDownIcon />}
          onClick={e => setTypeAnchor(e.currentTarget)}>
          {t('스케줄')}: {t(selectedTypeLabel)}
        </FilterButton>
        <Menu
          anchorEl={typeAnchor}
          open={!!typeAnchor}
          onClose={() => setTypeAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          // 트리거에서 열리고, 나갈 땐 더 빠르게 — 사용자가 결정한 뒤엔 기다리게 하지 않는다
          transitionDuration={{ enter: 180, exit: 120 }}
          slotProps={{
            paper: {
              sx: {
                mt: '6px',
                minWidth: '150px',
                borderRadius: '8px',
                border: '1px solid #E5E5E5',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
              },
            },
          }}>
          {typeSegments.map(segment => {
            const isSelected = typeFilter === segment.value;
            return (
              <MenuItem
                key={segment.value}
                selected={isSelected}
                onClick={() => {
                  onTypeFilterChange(segment.value);
                  setTypeAnchor(null);
                }}
                sx={{
                  fontSize: '15px',
                  padding: '10px 20px',
                  color: isSelected ? '#4AB3BC' : '#333',
                  fontWeight: isSelected ? 700 : 400,
                  '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: 'transparent' },
                }}>
                {t(segment.label)}
              </MenuItem>
            );
          })}
        </Menu>

        <FilterButton className="hdr-filter" isActive={selectedTrainerIds.length > 0} onClick={onTrainerFilterClick}>
          {t('임직원 필터')} {selectedTrainerIds.length > 0 && `(${selectedTrainerIds.length})`}
        </FilterButton>
      </HeaderRightGroup>
      </HeaderCountsRow>
    </PageContainer>
  );
};

export default PrivateLessonHeader;
